import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import type { IMediaMetaObjectQuery } from "@repo/shopifyV2/interface/metaobject";
import type { Image as ImageType, MediaImage, Metaobject, MetaobjectEdge } from "@repo/shopifyV2/types/storefront.types";

import Button from "@atoms/Button";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";
import Select from "@atoms/Select";

import type { ISortOption } from "@const/constant";
import { SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useHeaderScroll from "@hooks/useHeaderScroll";
import useLanguage from "@hooks/useLanguage";

import useMetaObjects from "@queries/useMetaObjects";

import { useLayoutStore } from "@store/layout";

export default function Stores() {
  const { setMeta, showFooter } = useLayoutStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort") || "new";

  const { isPC } = useCheckDevice();

  const { lang } = useLanguage();

  const STORE_OPTIONS = [{ name: "All Stores", value: "all", jp: "all" }];
  const DOMESTIC_OPTIONS = [
    { name: lang === "ko" ? "서울" : "Seoul", value: "seoul", jp: "seoul" },
    { name: lang === "ko" ? "부산" : "Busan", value: "busan", jp: "busan" },
    { name: lang === "ko" ? "광주" : "Gwangju", value: "gwangju", jp: "gwangju" },
  ];
  const OVERSEAS_OPTIONS = [
    { name: lang === "ko" ? "도쿄" : "Tokyo", value: "tokyo", jp: "東京" },
    { name: lang === "ko" ? "오사카" : "Osaka", value: "osaka", jp: "大阪" },
  ];

  const [sort, setSort] = useState<ISortOption>(sortParam === "old" ? SORT[0] : SORT[1]);

  const [option, setOption] = useState(STORE_OPTIONS[0]);

  const variables = useMemo(() => ({ type: "store", first: 250, sort: sort.value, reverse: sort.reverse }), [sort]);

  const { fetchMetaObjectMedia, metaObjectMediaData: data, metaObjectMediaLoading: loading } = useMetaObjects();

  useEffect(() => {
    void fetchMetaObjectMedia(variables);
  }, [variables]);

  useHeaderScroll();

  useEffect(() => {
    setMeta({ title: `Stores - ${DEFAULT_TITLE}` });
    showFooter(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sort, option]);

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <>
          <section
            className="pc:col-span-2 pc:top-30 sticky top-0 z-10 col-span-8 grid h-fit grid-cols-subgrid flex-col gap-y-1"
            id="filter"
          >
            {isPC ? (
              <div className="col-span-2 grid h-fit grid-cols-subgrid leading-none">
                <Button
                  as="text"
                  className="whitespace-nowrap"
                  onClick={() => {
                    const sortIndex = SORT.findIndex((item) => item.label === sort.label);
                    setSort(SORT[sortIndex === 0 ? 1 : 0]);
                    setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
                  }}
                >
                  {sort.label}
                </Button>
                <div className="flex flex-col gap-5">
                  <Button
                    as="text"
                    isActive={option.value === "all"}
                    onClick={() => {
                      setOption(STORE_OPTIONS[0]);
                    }}
                  >
                    All Stores
                  </Button>
                  <div className="flex flex-col gap-1.5">
                    <span>Domestic</span>
                    {DOMESTIC_OPTIONS.map((domestic) => (
                      <Button
                        as="text"
                        isActive={option.value === domestic.value}
                        key={domestic.value}
                        onClick={() => {
                          const findOption = DOMESTIC_OPTIONS.find((item) => item.value === domestic.value) ?? DOMESTIC_OPTIONS[0];
                          setOption(findOption);
                          // setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
                        }}
                      >
                        {domestic.name}
                      </Button>
                    ))}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span>Ovserseas</span>
                    {OVERSEAS_OPTIONS.map((oversea) => (
                      <Button
                        as="text"
                        isActive={option.value === oversea.value}
                        key={oversea.value}
                        onClick={() => {
                          const findOption = OVERSEAS_OPTIONS.find((item) => item.value === oversea.value) ?? OVERSEAS_OPTIONS[0];
                          setOption(findOption);
                          // setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
                        }}
                      >
                        {oversea.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="pc:hidden col-span-8 flex items-center justify-between gap-2 bg-white py-2.5">
                <div className="flex items-center gap-1">
                  <span className="text-base!">Viewing: </span>
                  <Select
                    as="square"
                    id="stores"
                    name="stores"
                    onChange={(e) => {
                      const findOption =
                        [...STORE_OPTIONS, ...DOMESTIC_OPTIONS, ...OVERSEAS_OPTIONS].find((item) => item.value === e.target.value) ??
                        STORE_OPTIONS[0];
                      setOption(findOption);
                    }}
                    options={[...STORE_OPTIONS, ...DOMESTIC_OPTIONS, ...OVERSEAS_OPTIONS]}
                    value={option.value}
                    variant="secondary"
                  />
                </div>
                <Select
                  as="square"
                  id="storeSort"
                  name="storeSort"
                  onChange={(e) => {
                    const sortIndex = SORT.findIndex((item) => item.label === `Sort by ${e.target.value}`);
                    setSort(SORT[sortIndex]);
                    setSearchParams({ sort: sortIndex === 1 ? "new" : "old" });
                  }}
                  options={SORT.map((item) => ({
                    ...item,
                    name: item.label.replace("Sort by ", ""),
                    value: item.label.replace("Sort by ", ""),
                  }))}
                  value={sort.label.replace("Sort by ", "")}
                  variant="secondary"
                />
              </div>
            )}
          </section>

          <section className="pc:col-span-10 col-span-8 grid grid-cols-subgrid gap-x-5 gap-y-20">
            {data.metaobjects.edges
              .filter(({ node }) => {
                if (option.value === "all") return true;
                const location = JSON.parse(node.fields.find((item) => item.key === "location")?.value || "[]") as string[];
                return location[0].includes(option.name) || location[0].includes(option.jp);
              })
              .map(({ node: store }: MetaobjectEdge) => {
                const images = store.fields
                  .find((item) => item.key === "images")
                  ?.references?.edges.map(({ node }) => (node as MediaImage).image) as ImageType[];

                const location = JSON.parse(store.fields.find((item) => item.key === "location")?.value || "[]") as string[];
                const tel = store.fields.find((item) => item.key === "tel" || item.key === "phone")?.value || "";
                const openingHour = (store.fields.find((item) => item.key === "opening_hour")?.value || "").replace(/\\n/g, "\n");
                const title = (store.fields.find((item) => item.key === "title")?.value || "").replace(/\\n/g, "\n");

                return (
                  <article className="tnt-gap-x pc:col-span-5 col-span-8 grid h-fit grid-cols-subgrid gap-y-2" key={store.handle}>
                    <Link className="pc:col-span-6 col-span-8" href={`${URLS.STORES}/${store.handle}`} state={store.id}>
                      <Image height={images[0].height || 2000} src={images[0].url} width={images[0].width || 2500} />
                    </Link>
                    <div className="pc:col-span-5 col-span-8 grid grid-cols-subgrid flex-col gap-y-5">
                      <div className="pc:col-span-5 col-span-8 flex justify-between">
                        <Link
                          className="text-title font-features-normal flex! flex-col leading-none whitespace-pre-line"
                          href={`${URLS.STORES}/${store.handle}`}
                          state={store.id}
                        >
                          {title}
                        </Link>
                        <Link as="square" href={`${URLS.STORES}/${store.handle}`} state={store.id} variant="secondary">
                          More View
                        </Link>
                      </div>
                      <div className="pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-1.5">
                        <span className="pc:col-span-1 pc:flex col-span-2 hidden">Address</span>
                        <Link as="text" className="pc:col-span-4 col-span-8" href={location[1] || ""}>
                          {location[0] || ""}
                        </Link>
                        <span className="pc:col-span-1 pc:flex col-span-2 hidden leading-none">Phone</span>
                        <Link as="text" className="pc:col-span-4 col-span-8 leading-none" href={`tel:${tel}`}>
                          {tel}
                        </Link>
                        <span className="pc:col-span-1 pc:flex col-span-2 -mt-0.5 hidden">Opening Hours</span>
                        <span className="pc:col-span-4 col-span-8 -mt-0.5 whitespace-pre-line">{openingHour}</span>
                      </div>
                    </div>
                  </article>
                );
              })}
          </section>
        </>
      )}
    </>
  );
}
